/**
 * Classroom calibration profiles for 6th-grade topics.
 *
 * A calibration profile describes the style of problem currently expected,
 * independent of what a generator is theoretically capable of. Generators
 * consult the profile to decide how often a feature appears, whether it is
 * allowed at all, and the numeric ranges / operation depth expected. Profiles
 * are refined as real classroom material arrives; topics without a profile
 * fall back to their own difficulty defaults.
 */

import type { TopicId } from "./curriculum";
import type { Difficulty } from "../topics/types";

export type Frequency = "never" | "rare" | "sometimes" | "frequent" | "always";

export const FREQUENCY_WEIGHT: Record<Frequency, number> = {
  never: 0,
  rare: 0.15,
  sometimes: 0.4,
  frequent: 0.75,
  always: 1,
};

export function frequencyProbability(f: Frequency): number {
  return FREQUENCY_WEIGHT[f];
}

export interface CalibrationFeatures {
  negativeNumbers?: Frequency;
  fractions?: Frequency;
  mixedNumbers?: Frequency;
  decimals?: Frequency;
  multiStep?: Frequency;
  largeNumbers?: Frequency;
}

export interface DepthRange {
  min: number;
  max: number;
}

export interface CalibrationProfile {
  topic: TopicId;
  note?: string;
  features: CalibrationFeatures;
  typicalDepth?: DepthRange;
  maxMagnitude?: number;
}

export const DEFAULT_CALIBRATION: Partial<Record<TopicId, CalibrationProfile>> = {};

/**
 * Recommended starting difficulty per topic.
 *
 * This is the difficulty the Practice setup preselects so a student lands on an
 * appropriate entry point instead of always starting at "standard". It is
 * seeded from the student's most recent diagnostic (NWEA MAP domain scores and
 * the Progress Learning per-standard results): topics scored at or near 0%
 * start at "easy" to rebuild the foundation, topics with partial traction start
 * at "standard", and the strongest areas start at "standard" as well (the
 * student can always bump difficulty up manually).
 *
 * A topic without an entry falls back to DEFAULT_START_DIFFICULTY. The student
 * always retains full manual control of difficulty in the setup screen; this
 * only changes the default selection.
 */
export const DEFAULT_START_DIFFICULTY: Difficulty = "standard";

const RECOMMENDED_START: Partial<Record<TopicId, Difficulty>> = {
  // --- Unit 1: Numerical Reasoning ---
  // Strongest NWEA domain (232). Rational-form work was weak on the diagnostic
  // (Order Rationals 13%, several forms 0%), so ease into those.
  "equivalent-forms": "easy", // 0% on diagnostic
  "compare-rationals": "easy", // 0% on diagnostic
  "order-rationals": "easy", // 13% on diagnostic
  "understand-integers": "standard", // 50% on diagnostic
  opposites: "standard",
  "absolute-value": "easy", // 0% on diagnostic
  "compare-order-integers": "standard",

  // --- Unit 2: Patterns, Algebra & Functional Reasoning (weakest NWEA, 207) ---
  "integer-operations": "standard", // 42% on diagnostic — some traction
  "fraction-operations": "easy", // 0% on diagnostic
  "decimal-operations": "easy", // 0% on diagnostic
  "multiplicative-inverse": "easy", // 0% on diagnostic
  "divide-fractions": "easy",
  "additive-inverse": "easy", // 0% on diagnostic
  "parts-expression": "easy", // 0-17% on diagnostic
  "evaluate-expressions": "easy", // 34% on diagnostic
  "exponent-expressions": "easy",
  "equivalent-expressions": "easy",
  "write-equations": "easy",
  "write-inequalities": "easy",
  "solve-equations": "easy", // 0% on diagnostic
  "solve-inequalities": "easy", // 0% on diagnostic
  "independent-dependent": "easy", // 25% on diagnostic
  "ratios-rates": "easy", // 0% on diagnostic
  "convert-units": "easy", // 0% on diagnostic

  // --- Unit 3: Measurement, Geometry & Spatial Reasoning (220, mid) ---
  "measuring-angles": "standard", // 67% on diagnostic — a bright spot
  "angle-relationships": "easy", // 0% on diagnostic
  "area-2d": "easy", // 0% on diagnostic
  "area-composite": "easy", // 0% on diagnostic
  "nets-3d": "easy", // 0% on diagnostic
  "surface-area": "easy", // 0% on diagnostic
  "volume-prisms": "easy", // 0% on diagnostic
  "coordinate-plane": "standard", // 43% on diagnostic
  "graphing-polygons": "easy", // 0% on diagnostic

  // --- Unit 4: Data, Probability & Statistical Reasoning (215, mid) ---
  "sample-size": "standard",
  "measures-center-spread": "standard", // 50% on diagnostic
  "box-plots": "easy", // 20% on diagnostic
  "data-shape": "easy", // 0% on diagnostic
  likelihood: "easy", // 0% on diagnostic
  "simple-probability": "standard", // 67% on diagnostic — a bright spot
  "complement-probability": "easy", // 0% on diagnostic
};

/**
 * The recommended starting difficulty for a topic's Practice setup. Falls back
 * to DEFAULT_START_DIFFICULTY when a topic has no explicit recommendation.
 */
export function recommendedStartDifficulty(topic: TopicId): Difficulty {
  return RECOMMENDED_START[topic] ?? DEFAULT_START_DIFFICULTY;
}

export function mergeCalibration(
  base: CalibrationFeatures,
  override: CalibrationFeatures | undefined,
): CalibrationFeatures {
  if (!override) return base;
  return { ...base, ...override };
}

export function featureFrequency(
  features: CalibrationFeatures,
  key: keyof CalibrationFeatures,
): Frequency {
  return features[key] ?? "sometimes";
}

export function getDefaultCalibration(topic: TopicId): CalibrationProfile | undefined {
  return DEFAULT_CALIBRATION[topic];
}
